import { ChangeEvent, JSX, MouseEvent, useState } from 'react';
import { useRecordContext } from 'react-admin';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    List,
    ListItemButton,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { EMAILS_PER_PAGE, useNokEditEmails } from "./hooks/nok-edit-emails.hooks";
import { EmailMessage, NokWebsite } from "../../../../types/entities";
import { AdminMiniEmailFrame } from "../NokMini/admin-mini/components/AdminMiniEmailFrame";
import { numeric } from "../../../dashboard/components/styles/dashboard-tokens";

/** Both panes share one height, so the list and the email scroll inside it, not the page. */
const PANE_HEIGHT: string = '70vh';

const pane = {
    height: PANE_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
} as const;

/**
 * en-GB, matching the date columns everywhere else in HQ. The API sends PHP's
 * `{ date: 'Y-m-d H:i:s.u' }`; anything unreadable is shown as it came.
 */
function formatSentAt(createdAt: EmailMessage['createdAt']): string {
    const raw: string = typeof createdAt?.date === 'string' ? createdAt.date : '';
    const date: Date = new Date(raw.replace(' ', 'T'));

    return Number.isNaN(date.getTime()) ? raw.replace('.000000', '') : date.toLocaleString('en-GB');
}

/**
 * A Nok website's inbox and outbox, with the chosen email beside the list.
 *
 * The body goes through `AdminMiniEmailFrame`, as on the email show page, never straight into the
 * page: it blanks remote images, because requesting a tracking pixel records an open, and an admin
 * reading a Nok's mail would otherwise log opens against the people it wrote to.
 */
export const NokEditEmails: () => JSX.Element = (): JSX.Element => {
    const record: NokWebsite = useRecordContext();
    const [activeEmail, setActiveEmail] = useState<EmailMessage>();
    const [filterValue, setFilterValue] = useState<string>('');
    const [activePanel, setActivePanel] = useState<string>('inbound');
    const { emails, loading, error, totalItems, pageNumber, nextPage, previousPage } = useNokEditEmails(
        record?.id.toString(),
        activePanel,
        filterValue
    );

    function handleActivePanelChange(_event: MouseEvent<HTMLElement>, value: string | null): void {
        // An exclusive group sends null when the selected button is clicked again.
        if (value !== null) {
            setActivePanel(value);
            setActiveEmail(undefined);
        }
    }

    function handleFilterChange(event: ChangeEvent<HTMLInputElement>): void {
        setFilterValue(event.target.value);
    }

    // The page size is the backend's, so it is read from the hook rather than repeated here.
    const firstShown: number = totalItems > 0 ? ((pageNumber - 1) * EMAILS_PER_PAGE) + 1 : 0;
    const lastShown: number = Math.min(pageNumber * EMAILS_PER_PAGE, totalItems);

    return (
        <Grid container spacing={ 2 }>
            <Grid size={ { xs: 12, md: 4, lg: 3 } }>
                <Paper variant="outlined" sx={ pane }>
                    <Stack spacing={ 1.5 } sx={ { p: 1.5, borderBottom: 1, borderColor: 'divider' } }>
                        <ToggleButtonGroup value={ activePanel }
                                           exclusive
                                           fullWidth
                                           size="small"
                                           color="primary"
                                           onChange={ handleActivePanelChange }
                                           aria-label="Which emails"
                        >
                            <ToggleButton value="inbound">{ "Inbox" }</ToggleButton>
                            <ToggleButton value="outbound">{ "Outbox" }</ToggleButton>
                        </ToggleButtonGroup>
                        <TextField size="small"
                                   fullWidth
                                   placeholder="Filter emails"
                                   value={ filterValue }
                                   onChange={ handleFilterChange }
                                   slotProps={ { htmlInput: { 'aria-label': 'Filter emails' } } } />
                    </Stack>

                    <Box sx={ { flex: 1, minHeight: 0, overflowY: 'auto' } }>
                        { (!loading && !error && totalItems <= 0) &&
                            <Typography variant="body2" color="text.secondary" sx={ { p: 2 } }>
                                { activePanel === 'inbound' ? "No emails received" : "No emails sent" }
                            </Typography>
                        }
                        { (emails?.length > 0) &&
                            <List dense disablePadding aria-label={ activePanel === 'inbound' ? "Inbox" : "Outbox" }>
                                { emails.map((email: EmailMessage): JSX.Element => {
                                    const isNew: boolean = email.status === 'new';

                                    return (
                                        <ListItemButton key={ email.id }
                                                        selected={ email.id === activeEmail?.id }
                                                        onClick={ (): void => setActiveEmail(email) }
                                                        divider
                                                        sx={ { display: 'block', py: 1 } }
                                        >
                                            <Stack direction="row" alignItems="center" spacing={ 1 }>
                                                <Typography variant="body2" noWrap sx={ { flex: 1, fontWeight: isNew ? 700 : 500 } }>
                                                    { email.subject || "(No subject)" }
                                                </Typography>
                                                { (isNew) &&
                                                    <Chip size="small" color="primary" label="New" sx={ { height: 18 } } />
                                                }
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" noWrap component="div">
                                                { activePanel === 'inbound' ? email.sender : email.recipient }
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" component="div" sx={ numeric }>
                                                { formatSentAt(email.createdAt) }
                                            </Typography>
                                        </ListItemButton>
                                    );
                                }) }
                            </List>
                        }
                    </Box>

                    <Stack direction="row" alignItems="center" justifyContent="space-between"
                           sx={ { px: 1.5, py: 0.5, borderTop: 1, borderColor: 'divider' } }>
                        <Typography variant="caption" color="text.secondary" sx={ numeric }>
                            { `${ firstShown }–${ lastShown } of ${ totalItems }` }
                        </Typography>
                        <Box>
                            <IconButton size="small"
                                        aria-label="Previous page"
                                        disabled={ pageNumber <= 1 }
                                        onClick={ previousPage }>
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small"
                                        aria-label="Next page"
                                        disabled={ lastShown >= totalItems }
                                        onClick={ nextPage }>
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Stack>
                </Paper>
            </Grid>

            <Grid size={ { xs: 12, md: 8, lg: 9 } }>
                <Paper variant="outlined" sx={ pane }>
                    { (loading) &&
                        <Stack alignItems="center" justifyContent="center" spacing={ 1.5 } sx={ { flex: 1 } }>
                            <CircularProgress size={ 28 } />
                            <Typography variant="body2" color="text.secondary">{ "Loading emails" }</Typography>
                        </Stack>
                    }
                    { (!loading && error) &&
                        <Box sx={ { p: 2 } }>
                            <Alert severity="error">{ "The emails could not be loaded. Try again in a moment." }</Alert>
                        </Box>
                    }
                    { (!loading && !error && activeEmail === undefined) &&
                        <Stack alignItems="center" justifyContent="center" sx={ { flex: 1 } }>
                            <Typography variant="body2" color="text.secondary">{ "Choose an email to read it." }</Typography>
                        </Stack>
                    }
                    { (!loading && !error && activeEmail !== undefined) &&
                        <>
                            <Box sx={ { px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' } }>
                                <Typography variant="h6" component="h3" sx={ { wordBreak: 'break-word' } }>
                                    { activeEmail.subject || "(No subject)" }
                                </Typography>
                                <Box component="dl" sx={ { m: 0, mt: 0.5, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.25 } }>
                                    <Typography component="dt" variant="caption" color="text.secondary">{ "From" }</Typography>
                                    <Typography component="dd" variant="caption" sx={ { m: 0, wordBreak: 'break-all' } }>{ activeEmail.sender }</Typography>
                                    <Typography component="dt" variant="caption" color="text.secondary">{ "To" }</Typography>
                                    <Typography component="dd" variant="caption" sx={ { m: 0, wordBreak: 'break-all' } }>{ activeEmail.recipient }</Typography>
                                    <Typography component="dt" variant="caption" color="text.secondary">{ "Date" }</Typography>
                                    <Typography component="dd" variant="caption" sx={ { m: 0, ...numeric } }>{ formatSentAt(activeEmail.createdAt) }</Typography>
                                </Box>
                            </Box>
                            <Box sx={ { flex: 1, minHeight: 0, overflowY: 'auto', p: 2 } }>
                                <AdminMiniEmailFrame html={ activeEmail.bodyHtml } text={ activeEmail.body } />
                            </Box>
                        </>
                    }
                </Paper>
            </Grid>
        </Grid>
    );
}
